import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '../../../lib/api-client';

import type { ConductAlertConfigWire } from '../../../lib/api-client';
import type {
  CannedResponseMetricsOverview,
  CannedResponseOperatorMetric,
  ConductAlertConfig,
} from '@lume/shared';
import type { UseMutationResult } from '@tanstack/react-query';

/**
 * Respostas Prontas do Operador — S4 (supervisor). Hooks das métricas de uso
 * + config do alerta de conduta. Mirror de `use-quality.ts` (chave-prefixo
 * dedicada + `staleTime` 60s) com o retry-guard e o gate-the-fetch de
 * `use-canned-responses.ts` (a cap `canned-response-analytics` é opt-in e o
 * /auth/me NÃO filtra optInRequired — o 403 é DETERMINÍSTICO, não re-tenta; o
 * gate-and-split da página o trata inline como "recurso indisponível").
 */
export const CANNED_ANALYTICS_KEY = ['canned-analytics'] as const;

/** Janela temporal (yyyy-mm-dd) — mesma do dashboard de qualidade (D5). */
export interface CannedAnalyticsWindow {
  periodFrom: string;
  periodTo: string;
}

function noRetryOn403(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && error.status === 403) return false;
  return failureCount < 3;
}

export function useCannedResponseMetricsOverview(
  w: CannedAnalyticsWindow,
  enabled = true,
): {
  data: CannedResponseMetricsOverview | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
} {
  const q = useQuery({
    queryKey: [...CANNED_ANALYTICS_KEY, 'overview', w.periodFrom, w.periodTo],
    queryFn: () => api.getCannedResponseMetricsOverview({ periodFrom: w.periodFrom, periodTo: w.periodTo }),
    enabled,
    staleTime: 60 * 1000,
    retry: noRetryOn403,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError, error: q.error };
}

export function useCannedResponseMetricsByOperator(enabled = true): {
  data: { data: CannedResponseOperatorMetric[] } | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
} {
  const q = useQuery({
    // Sem período: a by-operator usa a janela do alerta (o Engine resolve).
    queryKey: [...CANNED_ANALYTICS_KEY, 'by-operator'],
    queryFn: () => api.getCannedResponseMetricsByOperator(),
    enabled,
    staleTime: 60 * 1000,
    retry: noRetryOn403,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError, error: q.error };
}

export function useConductAlertConfig(enabled = true): {
  data: ConductAlertConfigWire | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
} {
  const q = useQuery({
    queryKey: [...CANNED_ANALYTICS_KEY, 'conduct-alert-config'],
    queryFn: () => api.getConductAlertConfig(),
    enabled,
    staleTime: 60 * 1000,
    retry: noRetryOn403,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError, error: q.error };
}

function useInvalidateAnalytics(): () => void {
  const qc = useQueryClient();
  // Invalida o prefixo inteiro: a config muda + o flag `alertFlagged` da
  // by-operator é RECOMPUTADO (limiar/janela/amostra mudaram).
  return () => void qc.invalidateQueries({ queryKey: CANNED_ANALYTICS_KEY });
}

export function useUpdateConductAlertConfig(): UseMutationResult<
  ConductAlertConfigWire,
  Error,
  { version: number; body: Partial<ConductAlertConfig> }
> {
  const invalidate = useInvalidateAnalytics();
  return useMutation({
    mutationFn: ({ version, body }: { version: number; body: Partial<ConductAlertConfig> }) =>
      api.updateConductAlertConfig(version, body),
    onSuccess: invalidate,
  });
}
